import React, { createContext, useContext, useState, ReactNode } from 'react';
import { useAddCommentsMutation, useUpdateCommentMutation } from '@/services/api';
import { MutableComment, MutableDesignBbox } from '../types/types';

interface CommentContextProps {
    isDrawing: boolean;
    isDrawingSpec: boolean;
    designBboxes: MutableDesignBbox[];
    commentData: Partial<MutableComment> | undefined;
    isEditing: boolean;
    submittalBboxes: SubmittalBbox[];
    editingComment: MutableComment | undefined;
    setCommentData: React.Dispatch<React.SetStateAction<Partial<MutableComment> | undefined>>;
    startDrawing: (comment?: MutableComment) => void;
    stopDrawing: (bboxes: number[][] | undefined) => void;
    startDrawingSpec: (comment?: MutableComment) => void;
    stopDrawingSpec: (designBboxes: MutableDesignBbox[]) => void;
    addDesignBbox: (bbox: number[], pageNum: number, specId: number) => void;
    deleteDesignBbox: (index: number) => void;
    deleteAllDesignBbox: () => void;
    setDesignBboxes: React.Dispatch<React.SetStateAction<MutableDesignBbox[]>>;
    addBbox: (bbox: number[], pageNum: number, submittalId: number) => void;
    deleteBbox: (index: number) => void;
    deleteAllBboxes: () => void;
    createComment: (newCommentData: MutableComment) => void;
    reset: () => void;
}

interface CommentProviderProps {
    children: ReactNode;
}

interface SubmittalBbox {
    pageNum: number;
    bbox: number[];
    submittalId: number;
}

const CommentContext = createContext<CommentContextProps | undefined>(undefined);

export const useCommentContext = (): CommentContextProps => {
    const context = useContext(CommentContext);
    if (!context) {
        throw new Error('useCommentContext must be used within a CommentProvider');
    }
    return context;
};

export const CommentProvider: React.FC<CommentProviderProps> = ({ children }) => {
    const [isDrawing, setIsDrawing] = useState(false);
    const [isDrawingSpec, setIsDrawingSpec] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [editingComment, setEditingComment] = useState<MutableComment | undefined>(undefined);
    const [designBboxes, setDesignBboxes] = useState<MutableDesignBbox[]>([]);
    const [submittalBboxes, setSubmittalBboxes] = useState<SubmittalBbox[]>([]);
    const [commentData, setCommentData] = useState<Partial<MutableComment>>();

    const [addComments] = useAddCommentsMutation();
    const [updateComment] = useUpdateCommentMutation();

    const createComment = (newCommentData: MutableComment) => {
        addComments([newCommentData]);
    };

    const startDrawing = (comment?: MutableComment) => {
        setIsDrawing(true);
        if (comment) {
            setIsEditing(true);
            setEditingComment(comment)
        }
    };

    const stopDrawing = (bboxes: number[][] | undefined) => {
        if (submittalBboxes && submittalBboxes.length > 0) {
            const pageNum = submittalBboxes[0].pageNum;
            const submittalId = submittalBboxes[0].submittalId;
            setCommentData(prevData => {
                if (prevData) {
                    return { ...prevData, pageNum, bboxes }
                }
                return {
                    submittalId,
                    pageNum,
                    bboxes
                }
            });
        }
        setIsDrawing(false);
        setEditingComment(undefined);
        if (isEditing && editingComment && submittalBboxes) {
            if (submittalBboxes.length > 0) {
                updateComment({
                    id: editingComment.id,
                    submittalId: editingComment.submittalId,
                    bboxes,
                    pageNum: submittalBboxes[0]?.pageNum,
                });
            } else {
                updateComment({
                    id: editingComment.id,
                    submittalId: editingComment.submittalId,
                    bboxes
                });
            }
            setSubmittalBboxes([]);
            setCommentData(prevData => {
                if (prevData) {
                    return {
                        ...prevData,
                        bboxes: undefined
                    }
                }
            });
        }
        setIsEditing(false);
    };

    const addBbox = (bbox: number[], pageNum: number, submittalId: number) => {
        const newBbox = { pageNum, bbox, submittalId };
        setSubmittalBboxes(prevBboxes => [...prevBboxes, newBbox]);
    };

    const deleteBbox = (index: number) => {
        setSubmittalBboxes((prevBboxes) => prevBboxes.filter((_, bboxesIndex) => bboxesIndex !== index));
    }

    const deleteAllBboxes = () => {
        setSubmittalBboxes([]);
    }

    const startDrawingSpec = (comment?: MutableComment) => {
        setIsDrawingSpec(true);
        if (comment) {
            setIsEditing(true);
            setEditingComment(comment)
        }
    };

    const stopDrawingSpec = (designBboxes: MutableDesignBbox[]) => {
        setDesignBboxes(designBboxes);
        setCommentData(prevData => {
            if (prevData) {
                return { ...prevData, designBboxes: designBboxes }
            } else {
                return {
                    designBboxes: designBboxes
                }
            }
        });
        setIsDrawingSpec(false);
        setEditingComment(undefined);
        if (isEditing && editingComment) {
            updateComment({
                id: editingComment.id,
                submittalId: editingComment.submittalId,
                designBboxes: designBboxes,
            });
            setDesignBboxes([]);
            setCommentData(prevData => ({
                ...prevData,
                designBboxes: undefined
            }));
        }
        setIsEditing(false);
    };

    const addDesignBbox = (bbox: number[], page: number, designId: number) => {
        const newDesignBbox = { page, bbox, design: { id: designId } };
        setDesignBboxes(prevBboxes => [...prevBboxes, newDesignBbox]);
    };

    const deleteDesignBbox = (index: number) => {
        setDesignBboxes((prevBboxes) => prevBboxes.filter((_, bboxesIndex) => bboxesIndex !== index));
    }

    const deleteAllDesignBbox = () => {
        setDesignBboxes([]);
    }

    const reset = () => {
        setIsDrawing(false);
        setIsDrawingSpec(false);
        deleteAllBboxes();
        deleteAllDesignBbox();
        setIsEditing(false);
        setEditingComment(undefined);
        setCommentData(undefined);
    }

    const value = {
        isDrawing,
        isDrawingSpec,
        designBboxes: designBboxes,
        commentData,
        isEditing,
        submittalBboxes,
        editingComment,
        setCommentData,
        addBbox,
        deleteBbox,
        deleteAllBboxes,
        setDesignBboxes,
        startDrawing,
        stopDrawing,
        startDrawingSpec,
        stopDrawingSpec,
        addDesignBbox,
        deleteDesignBbox,
        deleteAllDesignBbox,
        createComment,
        reset
    };

    return <CommentContext.Provider value={value}>{children}</CommentContext.Provider>;
};
